import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  FieldTimeOutlined,
  ShopOutlined,
  FireOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  ExpandOutlined,
  LeftOutlined,
  RightOutlined,
  AppstoreOutlined,
  CrownOutlined,
  InfoCircleOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  LineChartOutlined,
  CalendarOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import axios from "../../utils/axiosDash";
import { Modal } from "antd";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const STORE_STORAGE_KEY = "selectedStore";
const DEFAULT_STORE = "All Stores";
const REFRESH_MS = 30000;
const ACTIVE_WINDOW_MIN = 15; // a store is "Live" if it had an entry in the last N minutes

// Soft, light colours per store (assigned alphabetically so colours stay stable).
const STORE_PALETTE = [
  "#3B82F6", // blue
  "#14B8A6", // teal
  "#8B5CF6", // violet
  "#F472B6", // pink
  "#06B6D4", // cyan
  "#6366F1", // indigo
  "#84CC16", // lime
  "#64748B", // slate
];

const ENTRY_COLOR = "#60A5FA"; // light blue -> people coming IN
const PEAK_COLOR = "#2563EB"; // stronger blue to highlight the busiest hour / day
const TOTAL_LINE_COLOR = "#64748B";
const PREV_LINE_COLOR = "#CBD5E1";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, "0");
const dayKeyOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseDayKey = (k) => new Date(`${k}T00:00:00`);
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeek = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // Monday start
  return x;
};
const formatHour = (h) =>
  new Date(2000, 0, 1, h).toLocaleTimeString("en-IN", { hour: "numeric", hour12: true });

const formatClock = (d) =>
  d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });

const fmt = (n) => (Number.isFinite(n) ? n.toLocaleString("en-IN") : "—");
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const asDate = (v) => {
  const raw = v && typeof v === "object" && "$date" in v ? v.$date : v;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};
const toWall = (d) =>
  new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes());

const readStore = () => {
  try {
    return localStorage.getItem(STORE_STORAGE_KEY) || DEFAULT_STORE;
  } catch {
    return DEFAULT_STORE;
  }
};

const sumEntries = (events) => events.reduce((s, e) => s + e.entries, 0);
const pctChange = (now, before) => (before > 0 ? Math.round(((now - before) / before) * 100) : null);
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Flatten one-doc-per-camera-per-hour into one event per minute (footfall counts only). */
function flattenDocs(docs) {
  if (!Array.isArray(docs)) return [];
  const byMinute = new Map();
  docs.forEach((doc) => {
    const restaurant = doc.restaurant ? String(doc.restaurant) : "Unknown";
    const camera = doc.cameraIp
      ? `${doc.cameraIp}${doc.channel !== undefined && doc.channel !== null ? ` (ch ${doc.channel})` : ""}`
      : "Camera";
    const hourStart = asDate(doc.hourStart);
    const hourEnd = asDate(doc.hourEnd);

    (doc.minuteData || []).forEach((m) => {
      const raw = asDate(m.minute);
      if (!raw) return;
      if (hourStart && raw < hourStart) return;
      if (hourEnd && raw >= hourEnd) return;

      const ts = toWall(raw);
      const hour = hourStart ? toWall(hourStart).getHours() : ts.getHours();
      byMinute.set(`${restaurant}|${camera}|${raw.getTime()}`, {
        ts,
        hour,
        minOfDay: ts.getHours() * 60 + ts.getMinutes(),
        dayKey: dayKeyOf(ts),
        restaurant,
        camera,
        entries: toNum(m.entries) ?? 0,
      });
    });
  });
  return [...byMinute.values()].sort((a, b) => a.ts - b.ts);
}

/** Trends. Each row has `footfall` (total footfall) plus one key per store. */
function buildTrends(events, activeDate, storeNames) {
  const anchor = parseDayKey(activeDate);

  const makeRow = (label, subset) => {
    const row = { label, footfall: sumEntries(subset) };
    storeNames.forEach((s) => {
      row[s] = sumEntries(subset.filter((e) => e.restaurant === s));
    });
    return row;
  };

  const Daily = [];
  for (let i = 6; i >= 0; i--) {
    const d = addDays(anchor, -i);
    const key = dayKeyOf(d);
    Daily.push(
      makeRow(
        d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }),
        events.filter((e) => e.dayKey === key)
      )
    );
  }

  const Weekly = [];
  const anchorWeek = startOfWeek(anchor);
  for (let i = 3; i >= 0; i--) {
    const ws = addDays(anchorWeek, -7 * i);
    const key = dayKeyOf(ws);
    Weekly.push(
      makeRow(
        `Week of ${ws.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`,
        events.filter((e) => dayKeyOf(startOfWeek(e.ts)) === key)
      )
    );
  }

  const Monthly = [];
  for (let i = 5; i >= 0; i--) {
    const m = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
    Monthly.push(
      makeRow(
        m.toLocaleDateString("en-IN", { month: "short" }),
        events.filter((e) => e.ts.getFullYear() === m.getFullYear() && e.ts.getMonth() === m.getMonth())
      )
    );
  }
  return { Daily, Weekly, Monthly };
}

/**
 * Everything the page needs for a given (already store-filtered) event list.
 * `cutoff` = minute-of-day of the latest entry when viewing today, otherwise null.
 * It lets today be compared with the previous day up to the same time.
 */
function buildDashboard(events, activeDate, storeNames, cutoff) {
  const isToday = cutoff !== null;
  const prevKey = dayKeyOf(addDays(parseDayKey(activeDate), -1));
  const day = events.filter((e) => e.dayKey === activeDate);
  const prevDay = events.filter((e) => e.dayKey === prevKey);

  const total = sumEntries(day);
  const prevTotal = sumEntries(prevDay);
  const prevComparable = isToday ? sumEntries(prevDay.filter((e) => e.minOfDay <= cutoff)) : prevTotal;
  const deltaPct = pctChange(total, prevComparable);

  // Hourly (total + per store)
  const hourMap = new Map();
  day.forEach((e) => {
    const b = hourMap.get(e.hour) || { entries: 0, byStore: {} };
    b.entries += e.entries;
    b.byStore[e.restaurant] = (b.byStore[e.restaurant] || 0) + e.entries;
    hourMap.set(e.hour, b);
  });
  const hourly = [];
  if (hourMap.size) {
    const hrs = [...hourMap.keys()];
    for (let h = Math.min(...hrs); h <= Math.max(...hrs); h++) {
      const b = hourMap.get(h) || { entries: 0, byStore: {} };
      const row = { label: formatHour(h), entries: b.entries };
      storeNames.forEach((s) => {
        row[s] = b.byStore[s] || 0;
      });
      hourly.push(row);
    }
  }
  const peakHour = hourly.reduce((m, d) => (d.entries > 0 && (!m || d.entries > m.entries) ? d : m), null);
  const activeHours = hourly.filter((d) => d.entries > 0).length;
  const avgPerHour = activeHours ? Math.round(total / activeHours) : 0;

  // Camera-wise footfall
  const camMap = new Map();
  day.forEach((e) => camMap.set(e.camera, (camMap.get(e.camera) || 0) + e.entries));
  const cameras = [...camMap.entries()]
    .map(([name, entries]) => ({ name, entries }))
    .sort((a, b) => b.entries - a.entries);

  // Last 60 minutes
  const minutes = [];
  if (day.length) {
    const end = Math.floor(day[day.length - 1].ts.getTime() / 60000);
    const bucket = new Map();
    day.forEach((e) => {
      const k = Math.floor(e.ts.getTime() / 60000);
      bucket.set(k, (bucket.get(k) || 0) + e.entries);
    });
    for (let k = end - 59; k <= end; k++) {
      minutes.push({ label: formatClock(new Date(k * 60000)), entries: bucket.get(k) || 0 });
    }
  }
  const minutesHaveData = minutes.some((m) => m.entries > 0);
  const last60 = minutes.reduce((s, m) => s + m.entries, 0);
  const last15 = minutes.slice(-15).reduce((s, m) => s + m.entries, 0);

  // Running total through the day vs the previous day
  const hoursOf = (evs) => {
    const m = new Map();
    evs.forEach((e) => m.set(e.hour, (m.get(e.hour) || 0) + e.entries));
    return m;
  };
  const selH = hoursOf(day);
  const prevH = hoursOf(prevDay);
  const pace = [];
  const allHrs = [...selH.keys(), ...prevH.keys()];
  if (allHrs.length) {
    const lastSel = selH.size ? Math.max(...selH.keys()) : -1;
    let a = 0;
    let b = 0;
    for (let h = Math.min(...allHrs); h <= Math.max(...allHrs); h++) {
      a += selH.get(h) || 0;
      b += prevH.get(h) || 0;
      pace.push({ label: formatHour(h), selected: isToday && h > lastSel ? null : a, previous: b });
    }
  }
  const hasPrev = prevDay.length > 0;

  // Average footfall per weekday (full days only)
  const perDay = new Map();
  events.forEach((e) => {
    if (isToday && e.dayKey === activeDate) return;
    perDay.set(e.dayKey, (perDay.get(e.dayKey) || 0) + e.entries);
  });
  const wd = WEEKDAYS.map((label) => ({ label, sum: 0, days: 0 }));
  perDay.forEach((v, k) => {
    const i = (parseDayKey(k).getDay() + 6) % 7;
    wd[i].sum += v;
    wd[i].days += 1;
  });
  const weekdays = wd.map((w) => ({ label: w.label, avg: w.days ? Math.round(w.sum / w.days) : 0, days: w.days }));
  const weekdaysHaveData = weekdays.some((w) => w.avg > 0);
  const bestWeekday = weekdays.reduce((m, w) => (w.avg > 0 && (!m || w.avg > m.avg) ? w : m), null);

  // Heatmap (7 days)
  const heatDays = [];
  for (let i = 6; i >= 0; i--) heatDays.push(addDays(parseDayKey(activeDate), -i));
  const heatKeys = new Set(heatDays.map(dayKeyOf));
  const cell = new Map();
  let minH = 24;
  let maxH = -1;
  let maxV = 0;
  events.forEach((e) => {
    if (!heatKeys.has(e.dayKey) || e.entries === 0) return;
    const k = `${e.dayKey}|${e.hour}`;
    const v = (cell.get(k) || 0) + e.entries;
    cell.set(k, v);
    minH = Math.min(minH, e.hour);
    maxH = Math.max(maxH, e.hour);
    maxV = Math.max(maxV, v);
  });
  const heat =
    maxH >= 0
      ? {
          hours: Array.from({ length: maxH - minH + 1 }, (_, i) => minH + i),
          maxV,
          rows: heatDays.map((d) => ({
            label: d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }),
            key: dayKeyOf(d),
          })),
          cell,
        }
      : null;

  return {
    total,
    prevTotal,
    deltaPct,
    hourly,
    peakHour,
    activeHours,
    avgPerHour,
    cameras,
    minutes,
    minutesHaveData,
    last60,
    last15,
    pace,
    hasPrev,
    weekdays,
    weekdaysHaveData,
    bestWeekday,
    heat,
  };
}

/** One summary object per store, always computed from ALL events. */
function buildStoreStats(allEvents, activeDate, storeNames, cutoff) {
  const isToday = cutoff !== null;
  const prevKey = dayKeyOf(addDays(parseDayKey(activeDate), -1));
  const day = allEvents.filter((e) => e.dayKey === activeDate);
  const anchorTs = day.length ? day[day.length - 1].ts.getTime() : 0;

  // Shared hour range so every sparkline lines up.
  const allHours = day.map((e) => e.hour);
  const minH = allHours.length ? Math.min(...allHours) : 0;
  const maxH = allHours.length ? Math.max(...allHours) : -1;

  const list = storeNames.map((name) => {
    const evs = day.filter((e) => e.restaurant === name);
    const entries = sumEntries(evs);

    const hourEntries = new Map();
    evs.forEach((e) => hourEntries.set(e.hour, (hourEntries.get(e.hour) || 0) + e.entries));
    const spark = [];
    for (let h = minH; h <= maxH; h++) spark.push(hourEntries.get(h) || 0);
    let peakH = null;
    hourEntries.forEach((v, h) => {
      if (v > 0 && (peakH === null || v > hourEntries.get(peakH))) peakH = h;
    });
    const activeHours = [...hourEntries.values()].filter((v) => v > 0).length;

    const prevEvs = allEvents.filter((e) => e.restaurant === name && e.dayKey === prevKey);
    const prev = sumEntries(isToday ? prevEvs.filter((e) => e.minOfDay <= cutoff) : prevEvs);
    const lastActive = [...evs].reverse().find((e) => e.entries > 0)?.ts || null;
    const last60 = sumEntries(evs.filter((e) => e.ts.getTime() > anchorTs - 60 * 60000));

    return {
      name,
      entries,
      spark,
      peakHourLabel: peakH === null ? null : formatHour(peakH),
      avgPerHour: activeHours ? Math.round(entries / activeHours) : 0,
      last60,
      deltaPct: pctChange(entries, prev),
      lastActive,
    };
  });

  const entriesSum = list.reduce((s, x) => s + x.entries, 0);
  return list
    .map((s) => ({ ...s, share: entriesSum ? Math.round((s.entries / entriesSum) * 100) : 0 }))
    .sort((a, b) => b.entries - a.entries);
}

// Heatmap: very light blue (quiet) -> stronger blue (busy)
const heatClass = (v, max) => {
  if (!v) return "bg-slate-50 text-slate-300";
  const r = v / max;
  if (r >= 0.75) return "bg-blue-500 text-white";
  if (r >= 0.5) return "bg-blue-300 text-blue-950";
  if (r >= 0.25) return "bg-blue-200 text-blue-900";
  return "bg-blue-100 text-blue-800";
};

const minutesAgo = (d) => {
  if (!d) return null;
  const m = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
};

const TOOLTIP_STYLE = {
  borderRadius: 12,
  border: "1px solid #E2E8F0",
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(100,116,139,0.12)",
};
const AXIS_TICK = { fontSize: 11, fill: "#64748B" };
const GRID_STROKE = "#EEF2F7";
const AXIS_LABEL = { fontSize: 11, fill: "#94A3B8" };

// ---------------------------------------------------------------------------
// Presentational pieces
// ---------------------------------------------------------------------------
function StoreDot({ color, className = "" }) {
  return (
    <span
      className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${className}`}
      style={{ background: color }}
      aria-hidden="true"
    />
  );
}

/** Segmented bar + legend showing how a number splits across stores. */
function StoreSplit({ items, showBar = true }) {
  const total = items.reduce((s, i) => s + (i.value || 0), 0);
  return (
    <div className="mt-1 space-y-2">
      {showBar && total > 0 && (
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
          {items.map(
            (i) =>
              i.value > 0 && (
                <div
                  key={i.name}
                  title={`${i.name}: ${i.text}`}
                  style={{ width: `${(i.value / total) * 100}%`, background: i.color }}
                />
              )
          )}
        </div>
      )}
      <ul className="space-y-1.5">
        {items.map((i) => (
          <li key={i.name} className="flex items-center justify-between gap-2 text-xs">
            <span className="flex min-w-0 items-center gap-1.5 text-slate-500">
              <StoreDot color={i.color} />
              <span className="truncate">{i.name}</span>
            </span>
            <span className="shrink-0 font-semibold text-slate-700">{i.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatCard({ icon, tone, value, label, hint, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl ${tone}`}>
          {icon}
        </span>
        <p className="text-sm font-medium text-slate-500">{label}</p>
      </div>
      <p className="mt-3 truncate text-3xl font-semibold text-slate-800">{value}</p>
      {hint && <div className="mt-1 text-xs">{hint}</div>}
      {children && <div className="mt-3 border-t border-slate-100 pt-3">{children}</div>}
    </div>
  );
}

function Panel({ title, subtitle, icon, right, children, className = "" }) {
  return (
    <section className={`flex min-w-0 flex-col rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
      <div className="flex flex-col gap-2 border-b border-slate-100 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {icon}
            <h2 className="text-base font-semibold text-slate-800">{title}</h2>
          </div>
          {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function EmptyChart({ text }) {
  return (
    <div className="flex h-full min-h-[10rem] flex-col items-center justify-center gap-1 text-center text-slate-400">
      <InboxOutlined className="text-2xl" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

function Segmented({ options, value, onChange, label }) {
  return (
    <div
      className="flex self-start rounded-xl border border-slate-200 bg-slate-50 p-1"
      role="group"
      aria-label={label}
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
            value === o.value ? "bg-white text-blue-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ChartHint({ children }) {
  return (
    <p className="flex items-start gap-1.5 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
      <InfoCircleOutlined className="mt-0.5 text-blue-400" />
      <span>{children}</span>
    </p>
  );
}

function MinutesChart({ data, barRadius = 3 }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={32} />
        <YAxis
          tick={AXIS_TICK}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
          label={{ value: "People", angle: -90, position: "insideLeft", style: AXIS_LABEL }}
        />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#EFF6FF" }} />
        <Bar dataKey="entries" name="Footfall" fill={ENTRY_COLOR} radius={[barRadius, barRadius, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function Sparkline({ data, color }) {
  const max = Math.max(1, ...data);
  if (!data.length) return <div className="h-8" />;
  return (
    <div className="flex h-8 items-end gap-[2px]" aria-hidden="true">
      {data.map((v, i) => (
        <span
          key={i}
          className="flex-1 rounded-sm"
          style={{
            height: `${Math.max(v ? 12 : 4, (v / max) * 100)}%`,
            background: color,
            opacity: v ? 0.85 : 0.2,
          }}
        />
      ))}
    </div>
  );
}

function DeltaBadge({ pct, label = "vs prev day" }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-400">No prior day</span>;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        up ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"
      }`}
    >
      {up ? <RiseOutlined /> : <FallOutlined />}
      {Math.abs(pct)}% {label}
    </span>
  );
}

/** Filter chips: All Stores + one per store. */
function StoreTabs({ stats, value, onChange, totalEntries }) {
  return (
    <nav aria-label="Filter by store" className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
      <div className="flex w-max gap-2 pb-1 sm:w-auto sm:flex-wrap">
        <button
          type="button"
          onClick={() => onChange("all")}
          aria-pressed={value === "all"}
          className={`inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition ${
            value === "all"
              ? "border-blue-200 bg-blue-500 text-white shadow-sm"
              : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-600"
          }`}
        >
          <AppstoreOutlined />
          All Stores
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${
              value === "all" ? "bg-white/25 text-white" : "bg-slate-100 text-slate-500"
            }`}
          >
            {fmt(totalEntries)}
          </span>
        </button>
        {stats.map((s) => {
          const active = value === s.name;
          return (
            <button
              key={s.name}
              type="button"
              onClick={() => onChange(s.name)}
              aria-pressed={active}
              className={`inline-flex h-10 items-center gap-2 rounded-full border px-4 text-sm font-medium transition ${
                active
                  ? "bg-white text-slate-900 shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
              style={active ? { borderColor: s.color, boxShadow: `0 0 0 3px ${s.color}22` } : undefined}
            >
              <StoreDot color={s.color} />
              <span className="max-w-[10rem] truncate">{s.name}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{fmt(s.entries)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/** One card per store in the "All Stores" overview. */
function StoreCard({ s, rank, topEntries, isToday, lastHourLabel, deltaLabel, onSelect }) {
  const live = isToday && s.lastActive && Date.now() - s.lastActive.getTime() <= ACTIVE_WINDOW_MIN * 60000;
  const fill = topEntries > 0 ? Math.round((s.entries / topEntries) * 100) : 0;

  return (
    <button
      type="button"
      onClick={() => onSelect(s.name)}
      className="group flex w-full flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 sm:p-5"
      style={{ borderTop: `4px solid ${s.color}` }}
      aria-label={`View ${s.name} only`}
    >
      {/* Title row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          {rank === 1 && s.entries > 0 ? (
            <span
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-xs text-amber-500"
              title="Busiest store"
            >
              <CrownOutlined />
            </span>
          ) : (
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
              {rank}
            </span>
          )}
          <h3 className="truncate text-base font-semibold text-slate-800">{s.name}</h3>
        </div>
        {isToday ? (
          live ? (
            <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-600">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> Live
            </span>
          ) : (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">Quiet</span>
          )
        ) : (
          <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">Past day</span>
        )}
      </div>

      {/* Footfall */}
      <div className="mt-4 flex items-end justify-between gap-3">
        <div>
          <p className="text-3xl font-semibold tracking-tight text-slate-800">{fmt(s.entries)}</p>
          <p className="text-xs text-slate-400">{isToday ? "people in so far today" : "people in on this day"}</p>
        </div>
        <div className="w-24 shrink-0 sm:w-28">
          <Sparkline data={s.spark} color={s.color} />
          <p className="mt-1 text-right text-[10px] uppercase tracking-wide text-slate-400">Footfall by hour</p>
        </div>
      </div>

      <div className="mt-3">
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-2 rounded-full" style={{ width: `${fill}%`, background: s.color }} />
        </div>
        <p className="mt-1 text-xs text-slate-400">
          {rank === 1 ? "Busiest store" : `${fill}% of the busiest store`}
        </p>
      </div>

      {/* Numbers */}
      <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
        <div>
          <dt className="text-xs text-slate-400">{lastHourLabel}</dt>
          <dd className="text-base font-semibold text-slate-800">{fmt(s.last60)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-400">Avg / hour</dt>
          <dd className="text-base font-semibold text-slate-800">{fmt(s.avgPerHour)}</dd>
        </div>
        <div>
          <dt className="text-xs text-slate-400">Peak hour</dt>
          <dd className="truncate text-base font-semibold text-slate-800">{s.peakHourLabel || "—"}</dd>
        </div>
      </dl>

      {/* Footer */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <DeltaBadge pct={s.deltaPct} label={deltaLabel} />
        <span className="text-xs text-slate-400">{s.share}% of total footfall</span>
      </div>
      {s.lastActive && isToday && (
        <p className="mt-2 text-xs text-slate-400">Last activity {minutesAgo(s.lastActive)}</p>
      )}
      <span className="mt-3 text-xs font-medium text-blue-500 opacity-0 transition group-hover:opacity-100 group-focus:opacity-100">
        View only this store →
      </span>
    </button>
  );
}

/**
 * "Footfall so far" card.
 * Hero number = total footfall for the day. Shows the last-15-minute pace and how the day
 * compares with the previous day's final total. In the All Stores view, a per-store list follows.
 */
function EntriesSoFarCard({ title, dash, isToday, breakdown }) {
  if (!dash.total) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="truncate text-base font-semibold text-slate-800">
            {isToday ? "Footfall so far" : "Footfall"} — {title}
          </h2>
        </div>
        <EmptyChart text="No footfall recorded on this day" />
      </section>
    );
  }

  const { total, prevTotal, last15 } = dash;
  const vsPrevPct = prevTotal > 0 ? Math.round((total / prevTotal) * 100) : null;
  const barPct = vsPrevPct === null ? 0 : Math.min(100, vsPrevPct);
  const showSplit = breakdown && breakdown.length > 1;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="truncate text-base font-semibold text-slate-800">
          {isToday ? "Footfall so far" : "Day total"} — {title}
        </h2>
        {isToday ? (
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-600">Live</span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">Past day</span>
        )}
      </div>

      <div className="flex items-end gap-3">
        <p className="text-5xl font-semibold tracking-tight text-slate-800">{fmt(total)}</p>
        <div className="pb-1.5">
          <p className="text-xs text-slate-400">people counted</p>
          <p
            className={`mt-0.5 flex items-center gap-1 text-xs font-medium ${last15 > 0 ? "text-blue-500" : "text-slate-400"}`}
          >
            {last15 > 0 ? <RiseOutlined /> : <FieldTimeOutlined />}
            {last15 > 0 ? `+${fmt(last15)} in last 15 min` : "No footfall in last 15 min"}
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
          {vsPrevPct !== null ? (
            <div className="h-2.5 rounded-full bg-blue-400" style={{ width: `${barPct}%` }} />
          ) : showSplit ? (
            <div className="flex h-2.5 w-full">
              {breakdown.map(
                (b) =>
                  b.entries > 0 && (
                    <div
                      key={b.name}
                      title={`${b.name}: ${b.entries}`}
                      style={{ width: `${(b.entries / total) * 100}%`, background: b.color }}
                    />
                  )
              )}
            </div>
          ) : null}
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          {vsPrevPct !== null
            ? `${vsPrevPct}% of the previous day's total (${fmt(prevTotal)})`
            : "No previous day to compare with"}
        </p>
      </div>

      {showSplit && (
        <ul className="mt-4 space-y-2 border-t border-slate-100 pt-4">
          {breakdown.map((b) => (
            <li key={b.name} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-2 text-slate-600">
                <StoreDot color={b.color} />
                <span className="truncate">{b.name}</span>
              </span>
              <span className="shrink-0 font-semibold text-slate-800">
                {fmt(b.entries)} <span className="text-xs font-normal text-slate-400">· {b.share}%</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Skeleton() {
  return (
    <div className="animate-pulse space-y-6" aria-hidden="true">
      <div className="h-10 w-2/3 rounded-full bg-slate-200/70" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-40 rounded-2xl bg-slate-200/70" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-64 rounded-2xl bg-slate-200/70" />
        ))}
      </div>
      <div className="h-72 rounded-2xl bg-slate-200/70" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function FootfallDiningArea() {
  const [store, setStore] = useState(readStore);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [trendRange, setTrendRange] = useState("Daily");
  const [pickedDate, setPickedDate] = useState("");
  const [viewStore, setViewStore] = useState("all"); // in-page store filter
  const [minutesExpanded, setMinutesExpanded] = useState(false);
  const requestId = useRef(0);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    const currentStore = readStore();
    setStore(currentStore);
    const id = ++requestId.current;
    silent ? setRefreshing(true) : setLoading(true);
    try {
      const res = await axios.post("/get-empire-dining-footfall", { store: currentStore });
      if (id !== requestId.current) return;
      const body = res?.data;
      if (body?.success === false) throw new Error(body.message || "Request failed");
      setEvents(flattenDocs(body?.data));
      setError("");
      setLastUpdated(new Date());
    } catch (err) {
      if (id !== requestId.current) return;
      setError(err?.response?.data?.message || err?.message || "Could not load footfall data");
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(() => fetchData({ silent: true }), REFRESH_MS);
    const onStorage = (e) => {
      if (e.key === STORE_STORAGE_KEY) {
        setViewStore("all");
        fetchData();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(timer);
      window.removeEventListener("storage", onStorage);
      requestId.current++;
    };
  }, [fetchData]);

  // Stores present in the data, with stable colours.
  const storeNames = useMemo(() => [...new Set(events.map((e) => e.restaurant))].sort(), [events]);
  const colorOf = useCallback(
    (name) => STORE_PALETTE[Math.max(0, storeNames.indexOf(name)) % STORE_PALETTE.length],
    [storeNames]
  );
  const multi = storeNames.length > 1;
  const activeView = multi && storeNames.includes(viewStore) ? viewStore : "all";
  const showBreakdown = multi && activeView === "all";

  const todayKey = dayKeyOf(new Date());
  const defaultDate = useMemo(() => {
    if (!events.length || events.some((e) => e.dayKey === todayKey)) return todayKey;
    return events[events.length - 1].dayKey;
  }, [events, todayKey]);
  const activeDate = pickedDate || defaultDate;
  const isToday = activeDate === todayKey;

  // Minute-of-day of the latest activity today. Lets "today" be compared with the
  // previous day up to the same time instead of against its full-day total.
  const cutoff = useMemo(() => {
    if (!isToday) return null;
    const dayEvents = events.filter((e) => e.dayKey === activeDate);
    return dayEvents.length ? dayEvents[dayEvents.length - 1].minOfDay : -1;
  }, [events, activeDate, isToday]);

  const shiftDay = (n) => {
    const next = dayKeyOf(addDays(parseDayKey(activeDate), n));
    if (next > todayKey) return;
    setPickedDate(next);
  };

  // Always from ALL events — used by store tabs and store cards.
  const storeStats = useMemo(
    () => buildStoreStats(events, activeDate, storeNames, cutoff).map((s) => ({ ...s, color: colorOf(s.name) })),
    [events, activeDate, storeNames, cutoff, colorOf]
  );

  // Events for whatever the person is currently looking at.
  const viewEvents = useMemo(
    () => (activeView === "all" ? events : events.filter((e) => e.restaurant === activeView)),
    [events, activeView]
  );

  const dash = useMemo(
    () => buildDashboard(viewEvents, activeDate, storeNames, cutoff),
    [viewEvents, activeDate, storeNames, cutoff]
  );
  const trends = useMemo(() => buildTrends(viewEvents, activeDate, storeNames), [viewEvents, activeDate, storeNames]);
  const trendData = trends[trendRange];

  const dateLabel = parseDayKey(activeDate).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const hasDayData = dash.total > 0;
  const totalEntriesAll = storeStats.reduce((s, x) => s + x.entries, 0);
  const topEntries = storeStats.length ? storeStats[0].entries : 0;

  const titleStore = multi ? (activeView === "all" ? `All Stores (${storeNames.length})` : activeView) : store;
  const deltaLabel = isToday ? "vs same time yesterday" : "vs prev day";
  const lastHourLabel = isToday ? "Last hour" : "Final hour";
  const activeWeekday = WEEKDAYS[(parseDayKey(activeDate).getDay() + 6) % 7];

  const iconBtn =
    "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-40";

  // Split data for stat cards (only used when showBreakdown)
  const splitTotal = storeStats.map((s) => ({ name: s.name, color: s.color, value: s.entries, text: fmt(s.entries) }));
  const splitPeak = storeStats.map((s) => ({ name: s.name, color: s.color, value: 0, text: s.peakHourLabel || "—" }));
  const splitAvg = storeStats.map((s) => ({ name: s.name, color: s.color, value: 0, text: fmt(s.avgPerHour) }));
  const splitLast = storeStats.map((s) => ({ name: s.name, color: s.color, value: s.last60, text: fmt(s.last60) }));

  const trendRangeText =
    trendRange === "Daily" ? "last 7 days" : trendRange === "Weekly" ? "last 4 weeks" : "last 6 months";

  const cameraTotal = dash.cameras.reduce((s, c) => s + c.entries, 0);

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-sky-50 via-slate-50 to-white text-slate-800">
      <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        {/* Header */}
        <header className="mb-5 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Footfall &amp; Dining Area</h1>
            <p className="mt-1 text-sm text-slate-500">
              Empire Restaurant, {titleStore} — {dateLabel}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => shiftDay(-1)} className={iconBtn} aria-label="Previous day" title="Previous day">
                <LeftOutlined />
              </button>
              <input
                type="date"
                aria-label="Select date"
                value={activeDate}
                max={todayKey}
                onChange={(e) => e.target.value && setPickedDate(e.target.value)}
                className="h-9 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
              <button
                type="button"
                onClick={() => shiftDay(1)}
                disabled={isToday}
                className={iconBtn}
                aria-label="Next day"
                title="Next day"
              >
                <RightOutlined />
              </button>
            </div>

            {pickedDate && pickedDate !== defaultDate && (
              <button
                type="button"
                onClick={() => setPickedDate("")}
                className="h-9 rounded-lg bg-blue-50 px-3 text-sm font-medium text-blue-600 hover:bg-blue-100"
              >
                {defaultDate === todayKey ? "Back to today" : "Latest day"}
              </button>
            )}

            <button
              type="button"
              onClick={() => fetchData({ silent: true })}
              disabled={loading || refreshing}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:opacity-60"
            >
              <ReloadOutlined spin={refreshing} />
              Refresh
            </button>

            {lastUpdated && (
              <span className="w-full text-xs text-slate-400 sm:w-auto">Updated {formatClock(lastUpdated)}</span>
            )}
          </div>
        </header>

        {/* Error banner */}
        {error && events.length > 0 && (
          <div
            role="alert"
            className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600"
          >
            <ExclamationCircleOutlined />
            <span className="min-w-0 flex-1">Couldn't refresh: {error}. Showing the last loaded data.</span>
            <button type="button" onClick={() => fetchData({ silent: true })} className="font-medium underline">
              Retry
            </button>
          </div>
        )}

        {loading && !events.length ? (
          <Skeleton />
        ) : error && !events.length ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-3 rounded-2xl border border-rose-100 bg-white px-6 py-16 text-center shadow-sm"
          >
            <ExclamationCircleOutlined className="text-3xl text-rose-400" />
            <p className="text-base font-semibold text-slate-800">Footfall data didn't load</p>
            <p className="max-w-md text-sm text-slate-500">{error}</p>
            <button
              type="button"
              onClick={() => fetchData()}
              className="mt-2 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-600"
            >
              Try again
            </button>
          </div>
        ) : !events.length ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
            <InboxOutlined className="text-3xl text-slate-300" />
            <p className="text-base font-semibold text-slate-800">No footfall recorded for {store}</p>
            <p className="max-w-md text-sm text-slate-500">
              Counts will appear here once the cameras start reporting. Pick another store to check a different location.
            </p>
          </div>
        ) : (
          <main className="space-y-6">
            {/* Store filter */}
            {multi && (
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Choose a store</p>
                <StoreTabs
                  stats={storeStats}
                  value={activeView}
                  onChange={setViewStore}
                  totalEntries={totalEntriesAll}
                />
              </div>
            )}

            {/* Stat cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                icon={<TeamOutlined />}
                tone="bg-blue-50 text-blue-500"
                value={fmt(dash.total)}
                label="Total footfall"
                hint={
                  <span className="flex flex-wrap items-center gap-2 text-slate-400">
                    People counted
                    {dash.deltaPct !== null && (
                      <span className={dash.deltaPct >= 0 ? "text-emerald-600" : "text-rose-500"}>
                        {dash.deltaPct >= 0 ? "▲" : "▼"} {Math.abs(dash.deltaPct)}% {deltaLabel}
                      </span>
                    )}
                  </span>
                }
              >
                {showBreakdown && <StoreSplit items={splitTotal} />}
              </StatCard>

              <StatCard
                icon={<FieldTimeOutlined />}
                tone="bg-violet-50 text-violet-500"
                value={dash.peakHour?.label || "—"}
                label={showBreakdown ? "Peak hour (all stores)" : "Peak hour"}
                hint={
                  <span className="text-slate-400">
                    {dash.peakHour ? `${fmt(dash.peakHour.entries)} people during the busiest hour` : "Hour with the highest footfall"}
                  </span>
                }
              >
                {showBreakdown && <StoreSplit items={splitPeak} showBar={false} />}
              </StatCard>

              <StatCard
                icon={<BarChartOutlined />}
                tone="bg-sky-50 text-sky-500"
                value={fmt(dash.avgPerHour)}
                label="Average per hour"
                hint={
                  <span className="text-slate-400">
                    Across {fmt(dash.activeHours)} {dash.activeHours === 1 ? "hour" : "hours"} with entries
                  </span>
                }
              >
                {showBreakdown && <StoreSplit items={splitAvg} showBar={false} />}
              </StatCard>

              <StatCard
                icon={<ThunderboltOutlined />}
                tone="bg-teal-50 text-teal-500"
                value={fmt(dash.last60)}
                label={isToday ? "Last hour" : "Final hour of the day"}
                hint={<span className="text-slate-400">{fmt(dash.last15)} in the last 15 minutes</span>}
              >
                {showBreakdown && <StoreSplit items={splitLast} showBar={false} />}
              </StatCard>
            </div>

            {/* Store-by-store overview (All Stores view only) */}
            {showBreakdown && (
              <section aria-labelledby="store-overview">
                <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <h2 id="store-overview" className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                      <ShopOutlined className="text-blue-500" /> Store-Wise Overview
                    </h2>
                    <p className="text-xs text-slate-500">Ranked by footfall. Click a store to see only its data.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {storeStats.map((s, i) => (
                    <StoreCard
                      key={s.name}
                      s={s}
                      rank={i + 1}
                      topEntries={topEntries}
                      isToday={isToday}
                      lastHourLabel={lastHourLabel}
                      deltaLabel={deltaLabel}
                      onSelect={setViewStore}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Footfall so far + hourly */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <EntriesSoFarCard
                title={titleStore}
                dash={dash}
                isToday={isToday}
                breakdown={showBreakdown ? storeStats : null}
              />

              <Panel
                title={showBreakdown ? "Footfall by Hour, per Store" : "Footfall by hour"}
                subtitle={
                  showBreakdown
                    ? "Each colour is one store. The full bar is the combined total for that hour."
                    : "How many people counted during each hour. The busiest hour is darker."
                }
                className="lg:col-span-2"
              >
                <div className="h-60 p-3 sm:h-72 sm:p-4">
                  {hasDayData ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dash.hourly} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
                        <YAxis
                          tick={AXIS_TICK}
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                          label={{ value: "People", angle: -90, position: "insideLeft", style: AXIS_LABEL }}
                        />
                        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "#EFF6FF" }} />
                        {showBreakdown ? (
                          <>
                            <Legend wrapperStyle={{ fontSize: 12 }} />
                            {storeNames.map((s, i) => (
                              <Bar
                                key={s}
                                dataKey={s}
                                name={s}
                                stackId="stores"
                                fill={colorOf(s)}
                                radius={i === storeNames.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                              />
                            ))}
                          </>
                        ) : (
                          <Bar dataKey="entries" name="Footfall" radius={[6, 6, 0, 0]}>
                            {dash.hourly.map((d) => (
                              <Cell key={d.label} fill={d.label === dash.peakHour?.label ? PEAK_COLOR : ENTRY_COLOR} />
                            ))}
                          </Bar>
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyChart text="No footfall recorded on this day" />
                  )}
                </div>
                {hasDayData && dash.peakHour && (
                  <ChartHint>
                    Taller bar = busier hour. The busiest hour was <b className="text-slate-700">{dash.peakHour.label}</b> with{" "}
                    <b className="text-slate-700">{fmt(dash.peakHour.entries)}</b> people.
                  </ChartHint>
                )}
              </Panel>
            </div>

            {/* Pace vs previous day + weekday pattern */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <Panel
                title={isToday ? "Today vs yesterday" : "Selected day vs previous day"}
                subtitle="Running total of footfall through the day. Shows whether you are ahead of or behind the previous day."
                icon={<LineChartOutlined className="text-blue-500" />}
              >
                <div className="h-60 p-3 sm:h-64 sm:p-4">
                  {dash.pace.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dash.pace} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
                        <YAxis
                          tick={AXIS_TICK}
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                          label={{ value: "Footfall so far", angle: -90, position: "insideLeft", style: AXIS_LABEL }}
                        />
                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        {dash.hasPrev && (
                          <Line
                            type="monotone"
                            dataKey="previous"
                            name={isToday ? "Yesterday" : "Previous day"}
                            stroke={PREV_LINE_COLOR}
                            strokeWidth={2}
                            strokeDasharray="5 4"
                            dot={false}
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="selected"
                          name={isToday ? "Today" : "Selected day"}
                          stroke={PEAK_COLOR}
                          strokeWidth={3}
                          dot={{ r: 3, fill: "#fff", stroke: PEAK_COLOR, strokeWidth: 2 }}
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyChart text="No footfall to compare" />
                  )}
                </div>
                <ChartHint>
                  {dash.hasPrev
                    ? "If the solid line is above the dashed one, this day is busier than the previous day at the same hour."
                    : "There is no previous day in the data yet, so only this day is shown."}
                </ChartHint>
              </Panel>

              <Panel
                title="Busiest days of the week"
                subtitle="Average footfall per day for each weekday. Complete days only."
                icon={<CalendarOutlined className="text-blue-500" />}
              >
                <div className="h-60 p-3 sm:h-64 sm:p-4">
                  {dash.weekdaysHaveData ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dash.weekdays} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                        <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                        <YAxis
                          tick={AXIS_TICK}
                          axisLine={false}
                          tickLine={false}
                          allowDecimals={false}
                          label={{ value: "Avg footfall", angle: -90, position: "insideLeft", style: AXIS_LABEL }}
                        />
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          cursor={{ fill: "#EFF6FF" }}
                          formatter={(v, _n, p) => [`${fmt(v)} (from ${p.payload.days} ${p.payload.days === 1 ? "day" : "days"})`, "Average footfall"]}
                        />
                        <Bar dataKey="avg" name="Average footfall" radius={[6, 6, 0, 0]}>
                          {dash.weekdays.map((w) => (
                            <Cell key={w.label} fill={w.label === activeWeekday ? PEAK_COLOR : ENTRY_COLOR} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <EmptyChart text="Not enough history yet" />
                  )}
                </div>
                {dash.bestWeekday && (
                  <ChartHint>
                    <b className="text-slate-700">{dash.bestWeekday.label}</b> is the busiest day on average, with about{" "}
                    <b className="text-slate-700">{fmt(dash.bestWeekday.avg)}</b> people. The darker bar is the day you are viewing.
                  </ChartHint>
                )}
              </Panel>
            </div>

            {/* Trend */}
            <Panel
              title={showBreakdown ? "Footfall trend by store" : "Footfall trend"}
              subtitle={`Total footfall over the ${trendRangeText}. Use the buttons to change the range.`}
              right={
                <Segmented
                  label="Trend range"
                  value={trendRange}
                  onChange={setTrendRange}
                  options={Object.keys(trends).map((r) => ({ value: r, label: r }))}
                />
              }
            >
              <div className="h-64 p-3 sm:h-72 sm:p-4">
                {trendData.some((d) => d.footfall > 0) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                      <XAxis dataKey="label" tick={AXIS_TICK} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={16} />
                      <YAxis
                        tick={AXIS_TICK}
                        axisLine={false}
                        tickLine={false}
                        allowDecimals={false}
                        label={{ value: "Footfall", angle: -90, position: "insideLeft", style: AXIS_LABEL }}
                      />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {showBreakdown ? (
                        <>
                          {storeNames.map((s) => (
                            <Line
                              key={s}
                              type="monotone"
                              dataKey={s}
                              name={s}
                              stroke={colorOf(s)}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                            />
                          ))}
                          <Line
                            type="monotone"
                            dataKey="footfall"
                            name="All Stores combined"
                            stroke={TOTAL_LINE_COLOR}
                            strokeWidth={2}
                            strokeDasharray="5 4"
                            dot={false}
                          />
                        </>
                      ) : (
                        <Line
                          type="monotone"
                          dataKey="footfall"
                          name="Footfall"
                          stroke={ENTRY_COLOR}
                          strokeWidth={3}
                          dot={{ r: 4, fill: "#fff", stroke: ENTRY_COLOR, strokeWidth: 2 }}
                        />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyChart text="Not enough history for this range" />
                )}
              </div>
              <ChartHint>
                A line going up means more people than before; going down means fewer. Hover over a point to see the exact number.
              </ChartHint>
            </Panel>

            {/* Minute-by-minute + heatmap + cameras */}
            <div className="grid grid-cols-1 gap-6">
              <Panel
                title="Last 60 minutes of activity"
                subtitle="Each bar represents one minute and shows how many people counted."
                icon={<FieldTimeOutlined className="text-blue-500" />}
                right={
                  <button
                    type="button"
                    onClick={() => setMinutesExpanded(true)}
                    disabled={!dash.minutesHaveData}
                    title="Expand chart"
                    aria-label="Expand chart"
                    className="self-start rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-slate-500 shadow-sm hover:bg-slate-50 hover:text-blue-600 disabled:opacity-40"
                  >
                    <ExpandOutlined />
                  </button>
                }
              >
                <div className="h-56 p-3 sm:h-64 sm:p-4">
                  {dash.minutesHaveData ? (
                    <MinutesChart data={dash.minutes} />
                  ) : (
                    <EmptyChart text="No footfall reported in the last hour" />
                  )}
                </div>
              </Panel>

              <Panel
                title={`Busy Times, Last 7 Days${showBreakdown ? " (All Stores)" : ""}`}
                subtitle="Each square is one hour of one day. Darker blue = more people counted."
                icon={<FireOutlined className="text-blue-500" />}
                right={
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    Quiet
                    {["bg-slate-50 border border-slate-200", "bg-blue-100", "bg-blue-200", "bg-blue-300", "bg-blue-500"].map((c) => (
                      <span key={c} className={`h-3 w-3 rounded-sm ${c}`} />
                    ))}
                    Busy
                  </div>
                }
              >
                <div className="overflow-x-auto p-3 sm:p-4">
                  {dash.heat ? (
                    <div className="min-w-[420px]">
                      <div className="flex items-center gap-2">
                        <span className="w-16 shrink-0" />
                        <div
                          className="grid flex-1 gap-1"
                          style={{ gridTemplateColumns: `repeat(${dash.heat.hours.length}, minmax(0, 1fr))` }}
                        >
                          {dash.heat.hours.map((h) => (
                            <span key={h} className="text-center text-[10px] text-slate-500">
                              {formatHour(h).replace(" ", "").toLowerCase()}
                            </span>
                          ))}
                        </div>
                      </div>
                      {dash.heat.rows.map((row) => (
                        <div key={row.key} className="mt-1 flex items-center gap-2">
                          <span className="w-16 shrink-0 text-xs text-slate-500">{row.label}</span>
                          <div
                            className="grid flex-1 gap-1"
                            style={{ gridTemplateColumns: `repeat(${dash.heat.hours.length}, minmax(0, 1fr))` }}
                          >
                            {dash.heat.hours.map((h) => {
                              const v = dash.heat.cell.get(`${row.key}|${h}`) || 0;
                              return (
                                <div
                                  key={h}
                                  title={`${row.label}, ${formatHour(h)}: ${v} people`}
                                  className={`flex h-8 items-center justify-center rounded-md text-[10px] font-medium ${heatClass(
                                    v,
                                    dash.heat.maxV
                                  )}`}
                                >
                                  {v || ""}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyChart text="No footfall in the last 7 days" />
                  )}
                </div>
              </Panel>

              {dash.cameras.length > 1 && cameraTotal > 0 && (
                <Panel
                  title="Footfall by camera"
                  subtitle="Footfall counted by each entrance or camera on this day."
                  icon={<VideoCameraOutlined className="text-blue-500" />}
                >
                  <ul className="space-y-3 p-4">
                    {dash.cameras.map((c) => {
                      const share = Math.round((c.entries / cameraTotal) * 100);
                      return (
                        <li key={c.name}>
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <span className="min-w-0 truncate text-slate-600">{c.name}</span>
                            <span className="shrink-0 font-semibold text-slate-800">
                              {fmt(c.entries)} <span className="text-xs font-normal text-slate-400">· {share}%</span>
                            </span>
                          </div>
                          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                            <div className="h-2 rounded-full" style={{ width: `${share}%`, background: ENTRY_COLOR }} />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Panel>
              )}
            </div>
          </main>
        )}
      </div>

      {/* Expanded minute-by-minute chart */}
      <Modal
        open={minutesExpanded}
        onCancel={() => setMinutesExpanded(false)}
        footer={null}
        width={1000}
        centered
        destroyOnClose
        title={
          <div className="flex items-center gap-2">
            <FieldTimeOutlined className="text-blue-500" />
            <span>Last 60 minutes of activity — {titleStore}</span>
          </div>
        }
      >
        <div className="h-[320px] sm:h-[460px]">
          <MinutesChart data={dash.minutes} barRadius={4} />
        </div>
      </Modal>
    </div>
  );
}